<template>
  <div
    v-if="orderDetail"
    class="tw-flex tw-flex-col tw-w-[400px] tw-bg-white tw-drop-shadow-xl tw-rounded-lg tw-p-4 tw-mb-5"
    :class="{ 'tw-opacity-50': isFetching }"
  >
    <div class="tw-text-lg tw-font-bold">
      <a
        :href="`${ticketsBaseUrl}/v2/partners/${orderDetail.partner.id}`"
        class="hover:tw-underline tw-cursor-pointer"
        target="_blank"
      >
        {{ orderDetail.partner.full_name }}
      </a>
    </div>
    <div>week {{ orderDetail.week }} {{ orderDetail.year }}</div>
    <div
      class="tw-border tw-border-red-300 tw-bg-red-100 tw-p-1 tw-rounded-md"
      v-if="missingPartnerExactIds"
    >
      {{ missingPartnerExactIds }}
      <a
        :href="`${ticketsBaseUrl}/v2/partners/${orderDetail.partner.id}`"
        class="hover:tw-underline tw-cursor-pointer"
        target="_blank"
      >
        Pas aan
      </a>
    </div>
    <div class="tw-flex tw-mt-2">
      <span class="tw-font-bold tw-mr-2">Project:</span>
      <span>{{ project }}</span>
    </div>
    <div class="tw-flex tw-mt-2" v-if="exactTarget">
      <span class="tw-font-bold tw-mr-2">Boekt in:</span>
      <span>{{ exactTarget }}</span>
    </div>
    <div class="tw-flex tw-mt-2">
      <span class="tw-font-bold tw-mr-2">Tickets:</span>

      <a
        :href="`${ticketsBaseUrl}/v2/tickets?purchase_order_id=${orderDetail.id}`"
        class="hover:tw-underline tw-text-l tw-cursor-pointer"
        target="_blank"
      >
        Link to tickets
      </a>
    </div>
    <div class="tw-flex tw-flex-col tw-mt-2">
      <span class="tw-font-bold tw-mr-2">Products:</span>
      <div
        v-if="orderDetail.products"
        class="tw-mt-2 tw-space-y-2 tw-bg-gray-50 tw-p-3 tw-rounded tw-border tw-border-gray-200"
      >
        <div v-for="(amount, code) in orderDetail.products" :key="code" class="tw-flex">
          <div class="tw-text-gray-800 tw-font-bold tw-w-8">{{ amount }}</div>
          <div class="tw-text-gray-600 tw-text-sm">
            {{ getItemName(code) }}
          </div>
        </div>
      </div>
    </div>
    <div class="tw-flex tw-flex-col tw-mt-2">
      <span class="tw-font-bold tw-mr-2">Services:</span>
      <div
        v-if="orderDetail.services"
        class="tw-mt-2 tw-space-y-2 tw-bg-gray-50 tw-p-3 tw-rounded tw-border tw-border-gray-200"
      >
        <div v-for="(details, code) in orderDetail.services" :key="code" class="tw-flex">
          <div class="tw-text-gray-800 tw-font-bold tw-w-8">{{ details['quantity'] }}</div>
          <div class="tw-text-gray-600 tw-text-sm">
            {{ getItemName(code) }}
          </div>
        </div>
      </div>
    </div>

    <div class="tw-flex tw-flex-col tw-gap-2 tw-mt-4">
      <button
        v-if="!orderDetail.exact_purchase_order_id"
        class="tw-bg-blue-500 hover:tw-bg-blue-400 tw-text-white tw-rounded tw-py-1 tw-px-2 tw-font-semibold disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        @click="createPurchaseOrder"
        :disabled="isCreatingPurschaseOrder"
      >
        <span v-if="isCreatingPurschaseOrder">In progress...</span>
        <span v-else>Create Purchase Order in {{ exactTarget }}</span>
      </button>
      <div v-else class="tw-bg-green-600 tw-px-2 tw-py-1 tw-text-white tw-rounded">
        Purchase order is aangemaakt in {{ exactTarget }} (No. {{ orderDetail.exact_purchase_order_number }})
      </div>
      <button
        v-if="!orderDetail.exact_stock_count_id"
        class="tw-bg-blue-500 hover:tw-bg-blue-400 tw-text-white tw-rounded tw-py-1 tw-px-2 tw-font-semibold disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        @click="createStockCount"
        :disabled="isCreatingStockCount"
      >
        <span v-if="isCreatingStockCount">In progress...</span>
        <span v-else>Create Stock Count in {{ exactTarget }}</span>
      </button>
      <div v-else class="tw-bg-green-600 tw-px-2 tw-py-1 tw-text-white tw-rounded">
        Stock count is aangemaakt in {{ exactTarget }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router/composables'
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query'
import { notifications } from '@/util'
import fetchData from '@/util/fetch'
import { ticketsBaseUrl } from '@/logic/common/urls'

const route = useRoute()

const id = computed(() => route.params?.id)

const {
  data: orderDetail,
  isFetching,
  error,
} = useQuery({
  queryKey: computed(() => ['purchase-orders', route.params.id]),
  queryFn: async () => {
    try {
      const url = `/tickets/api/v1/purchase-orders/${route.params.id}`
      const result = await fetchData(url)
      if (result.status !== 'OK' || !result) {
        notifications.addNotification({
          message: `Failed to get the purchase order: ${result.statusText}`,
          type: 'danger',
        })
        return
      }
      return result?.data
    } catch (error) {
      notifications.addNotification({
        message: `Failed to get the  purchase order, ${e.message}`,
        type: 'danger',
      })
      throw new Error('Failed to fetch  purchase order data')
    }
  },
  refetchOnWindowFocus: false,
  keepPreviousData: true,
})

const projectsQuery = useQuery({
  queryKey: ['projects'],
  queryFn: fetchData('/config/api/v1/projects/?view=full'),
})

const projectConfig = computed(
  () => projectsQuery.data.value?.data.find((p) => p.id === orderDetail.value.project_id)
)

const project = computed(() => projectConfig.value?.name)

// The backend books in Exact Bouw when the project has an Exact Bouw project id, and
// in Exact Online otherwise; each needs its own partner ids.
const exactTarget = computed(() => {
  if (!projectConfig.value) return ''
  return projectConfig.value.exact_bouw_project_id ? 'Exact Bouw' : 'Exact Online'
})

const missingPartnerExactIds = computed(() => {
  if (!projectConfig.value) return ''
  const partner = orderDetail.value.partner
  if (exactTarget.value === 'Exact Bouw') {
    return partner.exact_bouw_contact_id ? '' : 'Er mist een Exact Bouw contact voor deze partner.'
  }
  return partner.exact_account_id && partner.exact_warehouse_id
    ? ''
    : 'Er mist een Exact Online leverancier en/of magazijn voor deze partner.'
})

const productsQuery = useQuery({
  queryKey: ['items', 'ticket-items'],
  queryFn: () =>
    fetchData(
      '/api/v1/tickets/items?limit=5000&item_type_ids=1&item_type_ids=2&item_type_ids=3&item_type_ids=4&item_type_ids=5&item_type_ids=8'
    ),
})

const getItemName = (code) => {
  return productsQuery.data.value?.data?.find((item) => item.code === code)?.name || code
}

const queryClient = useQueryClient()

const { mutate: createPurchaseOrder, isLoading: isCreatingPurschaseOrder } = useMutation({
  mutationFn: async () => {
    const result = await fetchData(
      `/tickets/api/v1/purchase-orders/${id.value}/exact-purchase-order`,
      {
        method: 'POST',
      }
    )
    return result
  },
  onSuccess: () => {
    notifications.addNotification({
      message: 'Purchase order created successfully',
      type: 'success',
    })

    queryClient.invalidateQueries(['purchase-orders'])
  },
  onError: (e) => {
    console.error('Error adding log', e)
    let message = e.message || e.detail || e
    if (Array.isArray(e.detail)) {
      message = e.detail[0].msg
    }
    notifications.addNotification({
      message: `Fout bij aanmaken PO in Exact: ${message}`,
      type: 'danger',
    })
  },
})

const { mutate: createStockCount, isLoading: isCreatingStockCount } = useMutation({
  mutationFn: async () => {
    const result = await fetchData(
      `/tickets/api/v1/purchase-orders/${id.value}/exact-stock-count`,
      {
        method: 'POST',
      }
    )
    return result
  },
  onSuccess: () => {
    notifications.addNotification({
      message: 'Stock count created successfully',
      type: 'success',
    })

    queryClient.invalidateQueries(['purchase-orders'])
  },
  onError: (e) => {
    console.error('Error adding stock count', e)
    let message = e.message || e.detail || e
    if (Array.isArray(e.detail)) {
      message = e.detail[0].msg
    }
    notifications.addNotification({
      message: `Fout bij aanmaken Stock Count in Exact: ${message}`,
      type: 'danger',
    })
  },
})
</script>
